import { z } from 'zod';
import {
  CandidaturaDtoSchema,
  CandidaturaAuditListResponseSchema,
  MeusEventosResponseSchema,
  CandidatosResponseSchema,
  CandidatosPageSchema,
  MyPendentesResponseSchema,
  BulkOperacaoResponseSchema,
  type CandidatarInput,
  type CancelarCandidaturaInput,
  type DispensarCandidaturaInput,
  type RejeitarInput,
  type CandidaturaDto,
  type CandidaturaAuditListResponse,
  type MeusEventosResponse,
  type CandidatosResponse,
  type CandidatosPage,
  type CandidaturaStatusFiltroProf,
  type CandidaturaStatusFiltroEmpresa,
  type MyPendentesResponse,
  type BulkAprovarInput,
  type BulkRejeitarInput,
  type BulkOperacaoResponse,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

const ConcluirSelecaoResponseSchema = z.object({
  rejeitadas: z.number().int(),
});

type ConcluirSelecaoResponse = z.infer<typeof ConcluirSelecaoResponseSchema>;

/**
 * REST surface for Candidatura entity. Mixed route prefixes:
 *   - /eventos/:id/vagas/:vid/candidatar  (POST PROFISSIONAL)
 *   - /candidaturas/me                    (GET  PROFISSIONAL)
 *   - /candidaturas/:id                   (GET  any authenticated; service decides ownership)
 *   - /eventos/:id/candidatos             (GET  EMPRESA)
 *   - /candidaturas/:id/aprovar           (PATCH EMPRESA)
 *   - /candidaturas/:id/rejeitar          (PATCH EMPRESA)
 *   - /eventos/:id/concluir-selecao       (POST EMPRESA)
 */
export class CandidaturasEndpoints {
  constructor(private readonly client: ApiClient) {}

  candidatar(
    eventoId: string,
    vagaId: string,
    input: CandidatarInput,
  ): Promise<CandidaturaDto> {
    return this.client.post(
      `/eventos/${eventoId}/vagas/${vagaId}/candidatar`,
      input,
      { responseSchema: CandidaturaDtoSchema },
    );
  }

  listarMinhas(filter?: {
    status?: CandidaturaStatusFiltroProf;
  }): Promise<MeusEventosResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (filter?.status !== undefined) query['status'] = filter.status;
    return this.client.get('/candidaturas/me', {
      query,
      responseSchema: MeusEventosResponseSchema,
    });
  }

  findOne(id: string): Promise<CandidaturaDto> {
    return this.client.get(`/candidaturas/${id}`, {
      responseSchema: CandidaturaDtoSchema,
    });
  }

  listarPorEvento(
    eventoId: string,
    filter?: { status?: CandidaturaStatusFiltroEmpresa },
  ): Promise<CandidatosResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (filter?.status !== undefined) query['status'] = filter.status;
    return this.client.get(`/eventos/${eventoId}/candidatos`, {
      query,
      responseSchema: CandidatosResponseSchema,
    });
  }

  aprovar(id: string): Promise<CandidaturaDto> {
    return this.client.patch(`/candidaturas/${id}/aprovar`, undefined, {
      responseSchema: CandidaturaDtoSchema,
    });
  }

  rejeitar(id: string, input?: RejeitarInput): Promise<CandidaturaDto> {
    return this.client.patch(`/candidaturas/${id}/rejeitar`, input ?? {}, {
      responseSchema: CandidaturaDtoSchema,
    });
  }

  /**
   * Phase 10 v1.1 — CANC-01. Profissional cancels own candidatura. `motivo`
   * is optional free text (max 200 chars). Server enforces D-1 cutoff (422
   * `cancel_window_closed`) and ownership (403 `ownership_required`).
   */
  cancelar(
    id: string,
    input?: CancelarCandidaturaInput,
  ): Promise<CandidaturaDto> {
    return this.client.post(`/candidaturas/${id}/cancelar`, input ?? {}, {
      responseSchema: CandidaturaDtoSchema,
    });
  }

  /**
   * Dispensa individual de candidato aprovado pela empresa (mínimo 24h antes).
   * Exige motivo (min 5, max 500) e requer papel ADMINISTRADOR ou COLABORADOR.
   */
  dispensar(
    id: string,
    input: DispensarCandidaturaInput,
  ): Promise<CandidaturaDto> {
    return this.client.post(`/candidaturas/${id}/dispensar`, input, {
      responseSchema: CandidaturaDtoSchema,
    });
  }

  concluirSelecao(eventoId: string): Promise<ConcluirSelecaoResponse> {
    return this.client.post(
      `/eventos/${eventoId}/concluir-selecao`,
      undefined,
      { responseSchema: ConcluirSelecaoResponseSchema },
    );
  }

  /**
   * Phase 12 v1.2 — AUDIT-CAND-01. Paginated audit list for a candidatura.
   * Server-side RBAC: profissional dono OR empresa
   * ADMINISTRADOR|COLABORADOR|VISUALIZADOR. Newest-first. Cursor pagination.
   */
  listAudit(
    id: string,
    opts?: { limit?: number; cursor?: string },
  ): Promise<CandidaturaAuditListResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (opts?.limit !== undefined) query['limit'] = opts.limit;
    if (opts?.cursor !== undefined) query['cursor'] = opts.cursor;
    return this.client.get(`/candidaturas/${id}/audit`, {
      query,
      responseSchema: CandidaturaAuditListResponseSchema,
    });
  }

  // ============================================================
  // Phase 17 v1.3 — FILA-01
  // ============================================================

  /**
   * Empresa manually promotes a specific pendente to aprovada. ABERTO-only;
   * 409 `candidatura_not_in_queue` when target is not pendente / wrong vaga /
   * vaga at capacity. 403 `ownership_required` if actor is not membro of
   * evento.empresaId. VISUALIZADOR is read-only — 403 papel_required.
   */
  promover(id: string): Promise<CandidaturaDto> {
    return this.client.post(`/candidaturas/${id}/promover`, undefined, {
      responseSchema: CandidaturaDtoSchema,
    });
  }

  /**
   * Profissional self-query: my pendente (ABERTO fila) candidaturas across
   * all eventos. Ordered by evento.dataInicio asc with `posicao` +
   * `totalNaFila` per row. Returns an empty array when actor has no
   * profissionalId or no pendentes.
   */
  listMyPendentes(): Promise<MyPendentesResponse> {
    return this.client.get('/candidaturas/me/pendentes', {
      responseSchema: MyPendentesResponseSchema,
    });
  }

  // ============================================================
  // Phase 30 v1.7 — PROTO-02 (CSV export) + PROTO-03 (bulk ops)
  // ============================================================

  /**
   * Bulk approve a specific set of candidaturas within an evento.
   * Backend returns 409 with BulkOverApprovalError payload when vagas
   * are exhausted before all IDs are processed.
   */
  bulkAprovar(
    eventoId: string,
    input: BulkAprovarInput,
  ): Promise<BulkOperacaoResponse> {
    return this.client.post('/candidaturas/bulk-approve', input, {
      query: { eventoId },
      responseSchema: BulkOperacaoResponseSchema,
    });
  }

  /**
   * Bulk reject a specific set of candidaturas within an evento.
   * Optional `motivo` is stored on each candidatura.
   */
  bulkRejeitar(
    eventoId: string,
    input: BulkRejeitarInput,
  ): Promise<BulkOperacaoResponse> {
    return this.client.post('/candidaturas/bulk-reject', input, {
      query: { eventoId },
      responseSchema: BulkOperacaoResponseSchema,
    });
  }

  /**
   * Approve all pendente candidaturas for an evento in a single atomic
   * server-side transaction. Server-side filter prevents stale-state races.
   * Returns 409 BulkOverApprovalError when total pendentes exceed remaining
   * vagas capacity.
   */
  aprovarTodosPendentes(eventoId: string): Promise<BulkOperacaoResponse> {
    return this.client.post(
      `/eventos/${eventoId}/candidatos/aprovar-todos-pendentes`,
      undefined,
      { responseSchema: BulkOperacaoResponseSchema },
    );
  }

  /**
   * Export candidatos list as CSV text (UTF-8 BOM + semicolon delimiter,
   * pt-BR column headers). Returns raw text string; caller is responsible
   * for creating a Blob and triggering browser download.
   *
   * @param eventoId - target evento
   * @param status - optional status filter; omit for all statuses
   */
  exportarCSV(eventoId: string, status?: string): Promise<string> {
    const query: Record<string, string | undefined> = {};
    if (status) query['status'] = status;
    return this.client.getRaw(`/eventos/${eventoId}/candidatos/export`, {
      query,
    });
  }

  /**
   * Phase 32 — DEBT-04 cursor-paginated candidatos list.
   * GET /eventos/:id/candidatos/page
   * Returns { items: CandidatoItem[], nextCursor: string|null }.
   * Pass nextCursor as cursor on the next call to fetch the following page.
   */
  listarPorEventoCursor(
    eventoId: string,
    opts?: { cursor?: string; limit?: number; status?: string },
  ): Promise<CandidatosPage> {
    const query: Record<string, string | number | undefined> = {};
    if (opts?.cursor !== undefined) query['cursor'] = opts.cursor;
    if (opts?.limit !== undefined) query['limit'] = opts.limit;
    if (opts?.status !== undefined) query['status'] = opts.status;
    return this.client.get(`/eventos/${eventoId}/candidatos/page`, {
      query,
      responseSchema: CandidatosPageSchema,
    });
  }

}
